// InviteDialog — send invites to an existing room from a search box.
//
// Every row renders a small invite icon button so admins can fire
// multiple invites without leaving the dialog. Already-joined members
// render as disabled "Already in room" rows so the user understands
// why a match can't be picked. Errors surface per-row as a one-line
// message under the search.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Send } from "lucide-react";

import { Dialog } from "~/components/Dialog";
import { DialogFooter, DialogHeader, ErrorLine } from "~/components/CreateRoomDialog";
import { GhostButton } from "~/components/MessageButtons";
import { UserSearch } from "~/components/UserSearch";
import { useMatrix } from "~/context/MatrixContext";
import { getMembers, getUserProfile, inviteUser, type UserDirectoryEntry } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

interface InviteDialogProps {
  roomId: string;
  roomName: string;
  onClose: () => void;
}

export function InviteDialog({ roomId, roomName, onClose }: InviteDialogProps) {
  const { client, searchUserDirectory } = useMatrix();
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const titleId = `${useId()}-title`;

  // Snapshot joined members on mount so already-in-room rows render
  // as disabled. A second read after each invite surfaces the new
  // membership and keeps the list honest.
  const [memberIds, setMemberIds] = useState<string[]>([]);
  useEffect(() => {
    if (!client) return;
    setMemberIds(getMembers(client, roomId).map((m) => m.userId));
  }, [client, roomId]);

  const disabledUserIds = useMemo(
    () => Array.from(new Set([...memberIds, ...invitedIds])),
    [memberIds, invitedIds],
  );

  const handleInvite = useCallback(
    async (user: UserDirectoryEntry) => {
      if (!client) return;
      setError(null);
      setStatus(null);
      try {
        await inviteUser(client, roomId, user.userId);
        setInvitedIds((prev) => new Set(prev).add(user.userId));
        setStatus(`Invited ${user.displayName}.`);
        setMemberIds(getMembers(client, roomId).map((m) => m.userId));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : `Couldn't invite ${user.displayName}. Try again.`,
        );
      }
    },
    [client, roomId],
  );

  return (
    <Dialog onClose={onClose} labelledBy={titleId}>
      <DialogHeader title={`Invite to #${roomName}`} titleId={titleId} onClose={onClose} />
      <div
        style={{
          padding: "var(--chat-dialog-body-padding)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-3)",
        }}
      >
        <UserSearch
          label={`Invite to ${roomName}`}
          placeholder="Search by name or @handle"
          onSearch={searchUserDirectory}
          resolveProfile={client ? (mxid) => getUserProfile(client, mxid) : undefined}
          onSelect={(u) => void handleInvite(u)}
          disabledUserIds={disabledUserIds}
          disabledLabel="In room"
          rowSuffix={(user) => (
            <InviteButton onClick={() => void handleInvite(user)} userName={user.displayName} />
          )}
          autoFocus
        />
        {status && <StatusLine>{status}</StatusLine>}
        {error && <ErrorLine>{error}</ErrorLine>}
      </div>
      <DialogFooter>
        <GhostButton onClick={onClose}>Close</GhostButton>
      </DialogFooter>
    </Dialog>
  );
}

function InviteButton({ onClick, userName }: { onClick: () => void; userName: string }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // The parent row captures mousedown to trigger selection; we
        // stop propagation so hitting this icon doesn't also run the
        // row's onSelect, which would double-invite.
        e.stopPropagation();
        onClick();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      aria-label={`Invite ${userName}`}
      className="chat-button"
      style={{
        width: "var(--chat-dialog-close-size)",
        height: "var(--chat-dialog-close-size)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        borderRadius: "var(--chat-radius-sm)",
        background: "transparent",
        color: "var(--chat-fg-secondary)",
        cursor: "pointer",
      }}
    >
      <Send size={14} strokeWidth={ICON_STROKE} />
    </button>
  );
}

function StatusLine({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="status"
      aria-live="polite"
      style={{
        margin: 0,
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      {children}
    </p>
  );
}
